import { IonButton, IonCol, IonIcon, IonRow } from '@ionic/react';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { chevronForward } from 'ionicons/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { FlattenedFolders } from './folder-flattener';
import style from './folder-flattener.module.scss';
import { FolderData } from './folders';

interface VerifyProps {
  folders: FolderData[];
  destination: FolderData;
  callback: (data: FlattenedFolders[]) => void;
}

const ITEMS_PER_PAGE = 500;
const PROGRESS_UPDATE_INTERVAL = 200;

const Verify: React.FC<VerifyProps> = ({ folders, destination, callback }) => {
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const [allFileData, setAllFileData] = useState<FlattenedFolders[]>([]);
  const [rowData, setRowData] = useState<FlattenedFolders[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
    currentFile: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [, setGridApi] = useState<any>();
  const [updateKey] = useState(0);
  const foldersHash = useMemo(
    () =>
      JSON.stringify(
        folders.map((f) => ({ path: f.path, fileCount: f.files?.length ?? 0 })),
      ),
    [folders],
  );
  const destinationPath = destination?.path ?? '';
  useEffect(() => {
    if (!destinationPath || !folders.length) return;

    let cancelled = false;

    const process = async () => {
      setIsProcessing(true);
      setAllFileData([]);
      setRowData([]);

      const seenNames = new Set<string>();
      const pathMap: FlattenedFolders[] = [];
      let processed = 0;

      const totalFiles = folders.reduce(
        (sum, f) => sum + (f.files?.length ?? 0),
        0,
      );
      setProcessingProgress({
        current: 0,
        total: totalFiles,
        percentage: 0,
        currentFile: '',
      });

      for (const folder of folders) {
        if (cancelled) break;
        const files = folder.files ?? [];

        for (let i = 0; i < files.length; i++) {
          if (cancelled) break;

          const file = files[i];
          const fileName = file.split(/[\\\/]/).pop() as string;

          let finalPath: string;
          if (!seenNames.has(fileName)) {
            seenNames.add(fileName);
            finalPath = `${destinationPath}/${fileName}`;
          } else {
            const timestamp = Date.now();
            finalPath = `${destinationPath}/Review/${timestamp}_${fileName}`;
          }

          pathMap.push({ current_path: file, new_path: finalPath });
          processed++;

          if (
            processed % PROGRESS_UPDATE_INTERVAL === 0 ||
            processed === totalFiles
          ) {
            setProcessingProgress({
              current: processed,
              total: totalFiles,
              percentage: Math.round((processed / totalFiles) * 100),
              currentFile: fileName,
            });
            await new Promise<void>((r) => setTimeout(r, 0));
          }
        }
      }

      if (cancelled) return;

      setProcessingProgress({
        current: totalFiles,
        total: totalFiles,
        percentage: 100,
        currentFile: '',
      });
      setAllFileData(pathMap);
      setRowData(pathMap.slice(0, ITEMS_PER_PAGE));
      setCurrentPage(1);
      setIsProcessing(false);
    };

    process().catch((err) => {
      if (!cancelled) {
        console.error('Verify processing error:', err);
        setIsProcessing(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [updateKey, foldersHash, destinationPath]);
  const totalPages = Math.ceil(allFileData.length / ITEMS_PER_PAGE);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    const start = (page - 1) * ITEMS_PER_PAGE;
    setRowData(allFileData.slice(start, start + ITEMS_PER_PAGE));
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'current_path',
        headerName: 'Current Path',
        flex: 1,
        filter: true,
        sortable: true,
        resizable: true,
      },
      {
        field: 'new_path',
        headerName: 'New Path',
        flex: 1,
        filter: true,
        sortable: true,
        resizable: true,
      },
    ],
    [],
  );

  const hasFiles = allFileData.length > 0;
  if (isProcessing) {
    return (
      <div className={style['select-folder']}>
        <div className="page-header">
          <h2>Verify</h2>
        </div>
        <div className={style['processing-overlay']}>
          <h3>Scanning files…</h3>
          <div className={style['progress-bar-track']}>
            <div
              className={style['progress-bar-fill']}
              style={{ width: `${processingProgress.percentage}%` }}
            />
          </div>
          <div className={style['processing-stats']}>
            <span>{processingProgress.percentage}%</span>
            <span>
              {processingProgress.current.toLocaleString()} /{' '}
              {processingProgress.total.toLocaleString()} files
            </span>
          </div>
          {processingProgress.currentFile && (
            <p className={style['processing-current-file']}>
              {processingProgress.currentFile}
            </p>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={style['select-folder']}>
      <div className="page-header">
        <h2>Verify</h2>
      </div>

      <p className={style['page-copy']}>
        Review the planned file moves below. Conflicts have been automatically
        routed to a <strong>Review/</strong> subfolder.
      </p>

      {allFileData.length >= ITEMS_PER_PAGE && (
        <div className={style['notice-banner']}>
          <strong>Large library:</strong> {allFileData.length.toLocaleString()}{' '}
          files — showing {ITEMS_PER_PAGE} per page. All files will be processed
          when you proceed.
        </div>
      )}

      {totalPages > 1 && (
        <div className={style['pagination']}>
          <button
            className={style['pagination-btn']}
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className={style['pagination-info']}>
            Page {currentPage} of {totalPages} &nbsp;·&nbsp;{' '}
            {allFileData.length.toLocaleString()} total files
          </span>
          <button
            className={style['pagination-btn']}
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      <div
        className={`ag-theme-alpine${theme === 'dark' ? '-dark' : ''} ${style['flatten-grid']}`}
      >
        <AgGridReact
          className={`ag-theme-alpine${theme === 'dark' ? '-dark' : ''} ${style['grid']}`}
          rowData={rowData}
          columnDefs={columnDefs}
          onGridReady={(p: GridReadyEvent) => setGridApi(p.api)}
          suppressScrollOnNewData
          rowBuffer={10}
          suppressRowClickSelection={true}
          suppressRowVirtualisation={false}
          suppressColumnVirtualisation={false}
          getRowId={(params) => params.data.current_path}
          animateRows={false}
          suppressDragLeaveHidesColumns
          overlayNoRowsTemplate="<span>No files to process</span>"
        />
      </div>
      <IonRow className={style['action-container']}>
        <IonCol
          size="12"
          sizeLg="6"
          offsetLg="6"
          className={`${style['align-center']} ${style['full-width']}`}
        >
          {hasFiles && (
            <IonButton
              style={{ marginTop: '20px' }}
              onClick={() => callback(allFileData)}
            >
              Flatten {allFileData.length.toLocaleString()} Files
              <IonIcon icon={chevronForward} slot="end" />
            </IonButton>
          )}
        </IonCol>
      </IonRow>
    </div>
  );
};

export default Verify;
